import { api } from "@/api/client";
import { convertToURLSearchParams } from "@/util";
import { PaginatedSearch } from "@/types";
import { Project } from "../project";
import { QualifierUser } from "../user";
import { Region } from "./region";
import { Welder } from "./welders";

export enum ScopeOfWorkStatus {
  New,
  Partial,
  Active,
  SentToAconex,
  Closed,
  Cancelled,
  Old,
}

export type ScopeOfWork = {
  id: string;
  number: string;
  description: string;
  project?: Project;
  reportDate: string;
  status: ScopeOfWorkStatus;
  weldingSpecialistId?: string;
  owner?: QualifierUser;
  commonScopeOfWorkId?: string;
  contractorId?: string;
  constructionCode?: string;
  inServiceWelding: boolean;
  wpsCode?: string;
  service?: string;
  materials?: string;
  thickestGrooveWeld?: string;
  smallestGrooveWeld?: string;
  continuityType?: string;
  allowQualificationsByNde: boolean;
  applyRepairWelderQualReqs: boolean;
  newYorkProject: boolean;
  regions?: Region[];
  welders?: Welder[];
  locked: boolean;
  template: boolean;
};

export type ScopeOfWorkListView = {
  id: string;
  number: string;
  description: string;
  projectId: string;
  projectNumber: string;
  status: ScopeOfWorkStatus;
  owner: string;
  weldingSpecialist: string;
  pinned: boolean;
};

export interface ScopeOfWorkFilters extends PaginatedSearch {
  favorite: boolean;
  owned: boolean;
  tier2: boolean;
  incomplete: boolean;
  templates: boolean;
  projectId: string;
  welderId: string;
  contractorId: string;
  searchString: string;
}

export type ScopeOfWorkValidation = {
  isDescriptionUnique: boolean;
};

export function getScopeOfWorkList(filters: Partial<ScopeOfWorkFilters>) {
  const params = convertToURLSearchParams(filters);
  return api.getPaginated<ScopeOfWorkListView[]>(`williams/scope?${params}`);
}

export function getScopeOfWorkById(id: string) {
  return api.get<ScopeOfWork>(`williams/scope/${id}`);
}

export function getScopeOfWorkByNumber(number: string) {
  return api.get<ScopeOfWork>(`williams/scope/number/${number}`);
}

export function getDefaultValues() {
  return api.get<ScopeOfWork>("williams/scope/default");
}

export function saveScopeOfWork(scope: ScopeOfWork) {
  return api.post<ScopeOfWork>("williams/scope", scope);
}

export function closeScopeOfWork(scope: ScopeOfWork) {
  return api.post<ScopeOfWork>("williams/scope/close", scope);
}

export function cancelScopeOfWork(scope: ScopeOfWork) {
  return api.post<ScopeOfWork>("williams/scope/cancel", scope);
}

export function validateScopeOfWork(scope: ScopeOfWork) {
  return api.post<ScopeOfWorkValidation>("williams/scope/validate", scope);
}

export function getAdvisories(scope: ScopeOfWork) {
  return api.post<string[]>("williams/scope/advisories", scope);
}

export function gethWelderAdvisories(scope: ScopeOfWork, welder: Welder) {
  return api.post<string[]>(`williams/scope/advisories/welder/${welder.welderId}`, scope);
}

export function togglePinned(id: string) {
  return api.post(`williams/scope/pin?id=${id}`);
}

export function deleteScopeOfWork(id: string) {
  return api.delete(`williams/scope/${id}`);
}
